import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qaqcApi, type PourCard } from '@/lib/apiClient';
import { POUR_TYPE_LABEL, useJobPicker } from './pourShared';
import { Button, Combobox } from '@/components';

/**
 * Office-entered pour card — for an order that no Pre-Pour Checklist will ever
 * arrive for, because the field leader phoned it in or the Formstack app was
 * down (PE ask, 2026-09-24). Without it the pour never reached this tab or the
 * schedule. The card it creates is an ordinary one: confirm / poured / cancel /
 * edit all work on it, and it's marked "Office entry" so nobody goes looking
 * for the form.
 *
 * Same inline-panel shape as the mix-design form. Pour type and date are
 * required (no date, no spot on the schedule); the rest can be filled in
 * from the drawer later.
 */
export default function AddPourCardForm({ projectId: fixedProjectId, onDone, onSaved }: {
  /** A job's own tab passes its job. The company-wide page omits it and the
   *  form asks which job the order is for. */
  projectId?: number;
  /** Closes the form (Cancel, and after a save). */
  onDone: () => void;
  /** The card just created. The pages open its drawer, so a new order can't
   *  land unseen behind whatever filter the list happens to be on. */
  onSaved?: (card: PourCard) => void;
}) {
  const qc = useQueryClient();
  // Job picker (company-wide page only).
  const [jobText, setJobText] = useState('');
  const jobPicker = useJobPicker(fixedProjectId === undefined);
  const projectId = fixedProjectId ?? jobPicker.idByLabel.get(jobText);
  const [form, setForm] = useState<Record<string, string>>({ pourType: 'main' });
  // Default on: the office is almost always entering an order it has just placed.
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // One id for this form's lifetime: a retried save (the first one committed
  // but the response failed) returns the same card instead of a duplicate.
  const [requestId] = useState(() => crypto.randomUUID());

  const rock = form.pourType === 'rock';

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => {
      const dto: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(form)) {
        // Wall-clock digits as UTC — the drawer's edit uses the same convention.
        if (k === 'pourDatetime') { dto[k] = v ? `${v}:00.000Z` : null; continue; }
        dto[k] = v.trim() === '' ? null : v.trim();
      }
      // Typed before switching the type to rock: no mix or placement for rock.
      if (rock) { delete dto.mix; delete dto.placement; }
      if (projectId === undefined) throw new Error('Pick the job this order is for');
      return qaqcApi.createPourCard({ ...dto, projectId, pourType: form.pourType, alreadyConfirmed, requestId });
    },
    onSuccess: (card) => {
      qc.invalidateQueries({ queryKey: ['qaqc-pours'] });
      qc.invalidateQueries({ queryKey: ['project-counts'] });
      onDone();
      onSaved?.(card);
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.message ?? 'Failed to save'),
  });

  const field: React.CSSProperties = {
    border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '6px 9px',
    font: 'var(--ew-fw-regular) 13px var(--ew-font)', background: '#fff', color: 'var(--ew-dark)', width: '100%',
  };
  const label: React.CSSProperties = { font: 'var(--ew-fw-semibold) 10.5px/1 var(--ew-font)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ew-n-500)', display: 'block', marginBottom: 4 };
  const input = (key: string, text: string, placeholder = '') => (
    <div>
      <label style={label}>{text}</label>
      <input style={field} value={form[key] ?? ''} onChange={set(key)} placeholder={placeholder} />
    </div>
  );

  return (
    <div style={{ padding: 'var(--ew-s-4)', background: 'var(--ew-n-50, #FAFAFA)', borderTop: '1px solid var(--ew-n-100)', borderBottom: '1px solid var(--ew-n-100)', marginTop: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--ew-n-500)', marginBottom: 12 }}>
        For an order placed without its Formstack form (Pre-Pour Checklist or Rock/Aggregate), phoned in by the field or entered while the form app was down.
        It shows on the job's QA/QC tab and the pour schedule like any other card.
      </div>
      {fixedProjectId === undefined && (
        <div style={{ marginBottom: 12, maxWidth: 420 }}>
          <label style={label}>Job *</label>
          <Combobox value={jobText} onChange={setJobText}
                    options={jobPicker.options}
                    placeholder="Type a job # or name…" />
          {jobText.trim() !== '' && projectId === undefined && (
            <div style={{ font: '11px var(--ew-font)', color: 'var(--ew-safety-700)', marginTop: 3 }}>Pick a job from the list.</div>
          )}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div>
          <label style={label}>Order type *</label>
          <select style={field} value={form.pourType} onChange={set('pourType')} autoFocus={fixedProjectId !== undefined}>
            {Object.entries(POUR_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>{rock ? 'Delivery' : 'Pour'} date &amp; time *</label>
          <input style={field} type="datetime-local" value={form.pourDatetime ?? ''} onChange={set('pourDatetime')} />
        </div>
        {rock ? (
          <>
            {input('element', 'Type of aggregate', '#57 stone, DGA')}
            {input('location', 'Delivery location', 'Building 2, east side')}
            {/* The unit goes in the text: rock is ordered in loads, tons or yards. */}
            {input('qtyText', 'Quantity (with unit)', 'e.g. 2 loads or 40 tons')}
            {input('vendor', 'Supplier', 'Aggregate supplier')}
          </>
        ) : (
          <>
            {input('element', 'Element', 'Footings, Grid A–C')}
            {input('location', 'Location', 'Building 2, east side')}
            {input('mix', 'Mix ID', '408SP2NH')}
            {input('qtyText', 'Qty (CY)', '80')}
            {input('vendor', 'Vendor', 'Irving Materials')}
            {input('placement', 'Placement', 'Pumped · 42m boom')}
          </>
        )}
        {input('costCode', 'Cost code', rock ? '31-23-33' : '03-31-05')}
        {input('superName', 'Field leader')}
        {input('dispatcherName', 'Dispatcher')}
        {input('dispatcherPhone', 'Dispatch phone')}
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={label}>Order notes</label>
        <input style={field} value={form.orderNotes ?? ''} onChange={set('orderNotes')}
               placeholder="Who called it in, what was agreed with the plant…" />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: 'var(--ew-dark)', cursor: 'pointer' }}>
        <input type="checkbox" checked={alreadyConfirmed} onChange={(e) => setAlreadyConfirmed(e.target.checked)} />
        I've already placed this order with the vendor (marks it Confirmed)
      </label>

      {error && (
        <div style={{ margin: '10px 0 0', padding: '7px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <Button variant="secondary" size="sm" onClick={onDone} disabled={save.isPending}>Cancel</Button>
        <Button variant="primary" size="sm" isLoading={save.isPending}
                onClick={() => { setError(null); save.mutate(); }}
                disabled={!form.pourDatetime || projectId === undefined}>
          Add order
        </Button>
      </div>
    </div>
  );
}
